import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GoalNotesCardProps {
  /** The title of the notes card */
  title: string;
  /** The criteria notes to display */
  criteriaNotes: CriteriaNote[];
  /** Additional MUI sx overrides for the card container */
  sx?: SxProps<Theme>;
}

// ---------------------------------------------------------------------------
// Internal sub-components
// ---------------------------------------------------------------------------

interface CriteriaNote {
  /** The label of the criteria note */
  label: string;
  /** The value of the criteria note */
  value: string | React.ReactNode;
}

function CriteriaRow({ label, value }: CriteriaNote) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Typography component="p" variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        component="p"
        variant="caption"
        color="text.primary"
        lineHeight="140%"
        letterSpacing="0.2px"
      >
        {value}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// GoalMasteryCriteriaCard
// ---------------------------------------------------------------------------

/**
 * Card displaying the mastery and generalization criterion definitions for a goal.
 *
 * @param props
 * @param props.title                    - The title of the notes card
 * @param props.criteriaNotes            - The criteria notes to display
 * @param props.sx                       - Additional MUI sx overrides for the card container
 */
const GoalNotesCard: React.FC<GoalNotesCardProps> = ({ title, criteriaNotes, sx }) => {
  return (
    <Stack
      direction="column"
      gap={4}
      component="section"
      aria-label={title}
      sx={[
        {
          bgcolor: "background.paper",
          border: "1px solid #E2E8F0",
          // 12px is not in theme.radius tokens (max is ex-large = 8px) — using design spec value
          borderRadius: "12px",
          px: 5, // 16px — theme.spacing(5)
          py: 7, // 24px — theme.spacing(7)
          width: "100%",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Typography component="h3" variant="body2">
        {title}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 4, width: "100%" }}>
        {criteriaNotes.map((note: CriteriaNote) => (
          <CriteriaRow key={note.label} label={note.label} value={note.value} />
        ))}
      </Box>
    </Stack>
  );
};

export default GoalNotesCard;
